import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faArrowLeft, faShoppingBag } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../../styles/theme';
import Footer from '../../Footer/Footer';

const SuccessContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
`;

const ContentContainer = styled.div`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
`;

const SuccessCard = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  box-shadow: ${props => props.theme.shadows.light};
  border: 1px solid ${props => props.theme.colors.greyBackground};
  margin: 2rem 0;
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  color: #4CAF50;
  margin-bottom: 1.5rem;
`;

const SuccessTitle = styled.h1`
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
  font-size: 2rem;
`;

const SuccessMessage = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const OrderDetails = styled.div`
  background: ${props => props.theme.colors.primaryLight};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  text-align: left;
`;

const OrderDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
    font-weight: 600;
    font-size: 1.1rem;
    padding-top: 0.5rem;
    border-top: 1px solid ${props => props.theme.colors.primary};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &.primary {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    
    &:hover {
      background: ${props => props.theme.colors.primaryDark};
      box-shadow: ${props => props.theme.shadows.medium};
    }
  }
  
  &.secondary {
    background: ${props => props.theme.colors.white};
    color: ${props => props.theme.colors.primary};
    border: 2px solid ${props => props.theme.colors.primary};
    
    &:hover {
      background: ${props => props.theme.colors.primary};
      color: ${props => props.theme.colors.white};
    }
  }
`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const orderNumber = searchParams.get('order');
  const paymentIntentId = searchParams.get('payment_intent');
  const total = searchParams.get('total');
  const paymentMethod = searchParams.get('payment_method');
  const status = searchParams.get('status');

  useEffect(() => {
    // Auto-redirect after 10 seconds
    const timer = setTimeout(() => {
      navigate('/duchess-collection');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <SuccessContainer>
      <ContentContainer>
        <SuccessCard>
          <SuccessIcon>
            <FontAwesomeIcon icon={faCheck} />
          </SuccessIcon>
          
          <SuccessTitle>
            {status === 'pending' ? 'Order Received!' : 'Payment Successful!'}
          </SuccessTitle>
          
          <SuccessMessage>
            {status === 'pending' ? (
              <>
                Thank you for your order! Your order has been received and is pending payment.
                {paymentMethod === 'stripe' && (
                  <>
                    <br /><br />
                    <strong>Next Steps:</strong><br />
                    • You will receive an email with payment instructions<br />
                    • Complete payment to confirm your order<br />
                    • Your order will be processed once payment is received
                  </>
                )}
              </>
            ) : (
              'Thank you for your order. Your payment has been processed successfully and you will receive a confirmation email shortly.'
            )}
          </SuccessMessage>

          {orderNumber && (
            <OrderDetails>
              <OrderDetailRow>
                <span>Order Number:</span>
                <span>#{orderNumber}</span>
              </OrderDetailRow>
              {paymentIntentId && (
                <OrderDetailRow>
                  <span>Payment ID:</span>
                  <span>{paymentIntentId}</span>
                </OrderDetailRow>
              )}
              {total && (
                <OrderDetailRow>
                  <span>Total {status === 'pending' ? 'Amount' : 'Paid'}:</span>
                  <span>£{parseFloat(total).toFixed(2)}</span>
                </OrderDetailRow>
              )}
              {paymentMethod && (
                <OrderDetailRow>
                  <span>Payment Method:</span>
                  <span>{paymentMethod === 'stripe' ? 'Stripe (Pending)' : paymentMethod}</span>
                </OrderDetailRow>
              )}
              {status === 'pending' && (
                <OrderDetailRow>
                  <span>Status:</span>
                  <span style={{ color: '#ff9800' }}>Pending Payment</span>
                </OrderDetailRow>
              )}
            </OrderDetails>
          )}

          <ActionButtons>
            <Button 
              className="primary" 
              onClick={() => navigate('/duchess-collection')}
            >
              <FontAwesomeIcon icon={faShoppingBag} />
              Continue Shopping
            </Button>
            
            <Button 
              className="secondary" 
              onClick={() => navigate('/duchess-collection')}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Collection
            </Button>
          </ActionButtons>

          <p style={{ marginTop: '2rem', fontSize: '0.9rem', color: '#666' }}>
            You will be automatically redirected to the shop in 10 seconds...
          </p>
        </SuccessCard>
      </ContentContainer>
      
      <Footer />
    </SuccessContainer>
  );
};

export default PaymentSuccess;